<template>
  <v-app>
    <v-app-bar v-if="showSidebar && isMobile" class="mobile-appbar" flat :height="56" :elevation="0">
      <v-app-bar-nav-icon
        class="mobile-appbar__toggle"
        aria-label="Open navigation"
        @click="mobileNavOpen = !mobileNavOpen" />
      <img src="@/assets/images/header-logo-white.png" alt="Lamar Health" class="mobile-appbar__logo" @click="goHome" />
    </v-app-bar>

    <AppSidebar v-if="showSidebar" :is-mobile="isMobile" :open="mobileNavOpen" @update:open="mobileNavOpen = $event" />
    <v-main class="d-flex flex-column" style="min-height: 100vh">
      <DesktopOnlyNotice
        v-if="showDesktopOnly"
        class="flex-grow-1"
        :title="desktopOnlyCopy.title"
        :message="desktopOnlyCopy.message" />
      <router-view v-else class="flex-grow-1" />
      <PriorFooter v-if="!isAuthRoute" />
    </v-main>
    <TableSettingsDialog v-if="showSidebar" v-model="tableSettingsStore.isDialogOpen" />
    <ConfirmDialog />
  </v-app>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore, useRequirementsStore, useTableSettingsStore } from "@/stores";
import { useResponsive } from "@/composables/useResponsive";
import AppSidebar from "@/components/AppSidebar.vue";
import PriorFooter from "@/components/PriorFooter.vue";
import TableSettingsDialog from "@/components/dashboard/TableSettingsDialog.vue";
import DesktopOnlyNotice from "@/components/dashboard/DesktopOnlyNotice.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const requirementsStore = useRequirementsStore();
const tableSettingsStore = useTableSettingsStore();
const { isMobile, isReadOnlyDevice } = useResponsive();

const mobileNavOpen = ref(false);

const publicRoutes = [
  "login",
  "complete-registration",
  "password-reset-request",
  "password-reset",
  "confirmEmail",
  "accept-invite",
  "sso-complete",
  "health-check",
];

// Pages rendered inside AuthShell; the shell supplies its own footer.
const authRoutes = [
  "login",
  "complete-registration",
  "password-reset-request",
  "password-reset",
  "confirmEmail",
  "accept-invite",
  "sso-complete",
];

const showSidebar = computed(() => {
  return authStore.loggedIn && !publicRoutes.includes(route.name);
});

const isAuthRoute = computed(() => authRoutes.includes(route.name));

// Full-screen data-entry that a phone can't present cleanly is gated on mobile.
// Internal/admin (ops) pages are intentionally NOT gated — they stay functional
// even if not pixel-perfect, since reps don't use them on a phone.
const desktopOnlyRoutes = new Set(["create-submission", "access-initiated-prior-auth"]);

const showDesktopOnly = computed(
  () => isReadOnlyDevice.value && showSidebar.value && desktopOnlyRoutes.has(route.name)
);

const desktopOnlyCopy = computed(() => {
  if (route.name === "create-submission") {
    return {
      title: "Create on desktop",
      message: "Creating a submission needs a full screen. Open this on a computer to continue.",
    };
  }
  return {
    title: "Best viewed on desktop",
    message: "This page is best reviewed on a computer. Open it on desktop to continue.",
  };
});

// Close the mobile drawer whenever the route changes (covers router-link nav).
watch(
  () => route.fullPath,
  () => {
    mobileNavOpen.value = false;
  }
);

const isDemo = computed(() => route.path.startsWith("/demo"));

function goHome() {
  const page = requirementsStore.clientConfig?.defaultPostLoginPage || "prior-auth";
  router.push({ name: isDemo.value ? `demo-${page}` : page });
}
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.mobile-appbar {
  background: $lh_sidebar_bg !important;
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.mobile-appbar__toggle {
  color: rgba(255, 255, 255, 0.9);
}

.mobile-appbar__logo {
  height: 26px;
  width: auto;
  object-fit: contain;
  cursor: pointer;
  margin-left: 4px;
}
</style>
